import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getConfig } from '../lib/config';
import { Spinner, EmptyState, ErrorAlert } from '../components/ui';
import * as publicApi from '../api/public';
import { getDonatePage } from '../api/payments';
import { ui, cn } from '../lib/ui';

export default function OrgLanding() {
  const { orgSlug } = useParams();
  const { productName } = getConfig();

  const [org, setOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [acceptsDonations, setAcceptsDonations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [orgData, eventsData, videosData, donatePage] = await Promise.all([
        publicApi.getOrgProfile(orgSlug),
        publicApi.listOrgEvents(orgSlug, { limit: 6 }).catch(() => ({ events: [] })),
        publicApi.listOrgVideos(orgSlug, { limit: 6 }).catch(() => ({ videos: [] })),
        getDonatePage(orgSlug).catch(() => ({ available: false })),
      ]);
      if (id !== runIdRef.current) return;
      setOrg(orgData);
      setEvents(eventsData.events || []);
      setVideos(videosData.videos || []);
      setAcceptsDonations(Boolean(donatePage?.available));
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Organization not found');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={ui.publicPage}>
        <Spinner />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className={ui.publicPage}>
        <div className={ui.publicContainer}>
          <ErrorAlert message={error || 'Organization not found'} />
          <p className="mt-4 text-center">
            <Link to="/login" className={ui.btnPrimary}>
              Sign in to {productName}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.publicPage}>
      <header className={ui.publicHeader}>
        <div className={ui.publicContainer}>
          <div className={ui.publicOrgHero}>
            {org.logoUrl && (
              <img src={org.logoUrl} alt={`${org.name} logo`} className={ui.publicOrgLogo} />
            )}
            <div>
              <h1 className="m-0 text-title-l">{org.name}</h1>
              {org.description && (
                <p className="mt-2 mb-0 text-dcon-ink-body">
                  {org.description}
                </p>
              )}
              {org.memberCount > 0 && (
                <p className={cn(ui.muted, 'mt-1 mb-0 text-body-s')}>
                  {org.memberCount} {org.memberCount === 1 ? 'member' : 'members'}
                </p>
              )}
            </div>
          </div>
          <div className={ui.publicHeaderActions}>
            <Link to={`/o/${orgSlug}/login`} className={ui.btnPrimary}>
              Sign in
            </Link>
            <Link to={`/o/${orgSlug}/register`} className={ui.btnSecondary}>
              Join
            </Link>
            {acceptsDonations && (
              <Link to={`/o/${orgSlug}/donate`} className={ui.btnAccent}>
                Donate
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className={cn(ui.publicContainer, 'pt-8')}>
        {org.audienceConfig?.events?.publicVisibility && events.length > 0 && (
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="m-0 text-title-m">Upcoming events</h2>
              <Link to={`/o/${orgSlug}/events`} className={ui.btnSecondary}>
                View all
              </Link>
            </div>
            <div className={cn(ui.grid, ui.gridMd2)}>
              {events.map(evt => (
                <Link
                  key={evt.eventId}
                  to={`/o/${orgSlug}/events/${evt.eventId}`}
                  className={cn(ui.card, ui.publicEventCard, 'text-inherit no-underline hover:no-underline')}
                >
                  <h3 className="mt-0 mb-2 text-title-s">{evt.name}</h3>
                  {evt.startsAt && (
                    <p className={cn('m-0 text-body-s', ui.muted)}>
                      {new Date(evt.startsAt).toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                  {evt.venue && (
                    <p className="mt-1 mb-0 text-body-s">
                      {evt.venue}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}

        {events.length === 0 && org.audienceConfig?.events?.publicVisibility && (
          <EmptyState message="No upcoming public events at this time." />
        )}

        {org.audienceConfig?.videos?.previewVideos > 0 && videos.length > 0 && (
          <section className="mt-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="m-0 text-title-m">Videos</h2>
              <Link to={`/o/${orgSlug}/videos`} className={ui.btnSecondary}>
                View all
              </Link>
            </div>
            <div className={cn(ui.grid, ui.gridMd2)}>
              {videos.map(v => (
                <div
                  key={v.videoId}
                  className={cn(ui.card, 'p-4!')}
                >
                  {v.thumbnailUrl && (
                    <img
                      src={v.thumbnailUrl}
                      alt={v.title}
                      className="mb-3 aspect-video w-full rounded-dcon-md object-cover"
                    />
                  )}
                  <h3 className="mt-0 mb-2 text-title-s">{v.title}</h3>
                  {v.description && (
                    <p
                      className={cn('m-0 line-clamp-2 text-body-s', ui.muted)}
                    >
                      {v.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className={ui.publicFooter}>
        <div className={ui.publicContainer}>
          <p className={cn(ui.muted, 'm-0 text-body-s')}>
            Powered by {productName}
          </p>
        </div>
      </footer>
    </div>
  );
}
